import React, { useState, useEffect, useRef, useCallback } from 'react';
import authService from '../services/auth';
import PoiCard from './poicard'; 
import { calculateDistance } from './utils'; // Importa la funzione Haversine
import config from "../config";
import BackButton from './BackButton';
import PoiCardList from './poicardlist';
import DOMPurify from 'dompurify';
import LoadingSkeletonList from './LoadingSkeletonList';

const List = () => {

    // State per memorizzare i dati dal servizio REST
    const [pointsOfInterest, setPointsOfInterest] = useState([]);
    const [loading, setLoading] = useState(true);
    const [taxonomyData, setTaxonomyData] = useState({});
    const [userLocation, setUserLocation] = useState(null);
    const [sortOption, setSortOption] = useState('distance'); // Ordina per distanza di default
    const [selectedCategory, setSelectedCategory] = useState(null); // Filtro per categoria

    const [selectedLang] = useState(localStorage.getItem('language') || config.languages.default);

    // Stato per memorizzare i target_id delle tassonomie già caricate
    const loadedTaxonomyIds = useRef(new Set());
    // URL del backend Drupal
    const apiUrl = `${config.API_URL}/poi/list`;
  
    // Funzione per ottenere i dati dal servizio REST con Axios
    const fetchPointsOfInterest = async () => {
        try {
            const response = await authService.get(apiUrl);
            setPointsOfInterest(response);
            setLoading(false);

            // Tassonomie da caricare (evita richieste duplicate)
            const taxonomyRequests = [];
            response.forEach(poi => {
                const taxonomyId = poi.field_tipologia_poi?.[0]?.target_id;

                if (taxonomyId && !loadedTaxonomyIds.current.has(taxonomyId)) {
                taxonomyRequests.push(
                    authService.get(config.API_ENDPOINTS.getTaxonomyTerm(taxonomyId, selectedLang)).then(response => {
                    const term = response;
                    setTaxonomyData(prev => ({
                        ...prev,
                        [term.tid[0].value]: {
                        title: term.name[0].value,
                        icon: term.field_icona?.[0]?.url || null,
                        pin: term.field_pin?.[0]?.url || null,
                        },
                    }));
                    }).catch(error => {
                    console.error(`Errore nel caricamento della tassonomia ${taxonomyId}:`, error);
                    // Rimuovi l'id dal set in caso di errore per consentire tentativi futuri
                    loadedTaxonomyIds.current.delete(taxonomyId);
                    })
                );
                }
                loadedTaxonomyIds.current.add(taxonomyId);        
            });

            await Promise.all(taxonomyRequests);

        } catch (error) {
            console.error('Errore durante il recupero dei dati:', error);
            setLoading(false);
        }
    };

    const fetchUserLocation = useCallback(() => {
        if (!navigator.geolocation) return null;
    
        const options = {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 30000  // 30 secondi come concordato
        };
    
        const watchId = navigator.geolocation.watchPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            if (isFinite(latitude) && isFinite(longitude)) {
              setUserLocation({ latitude, longitude });
            }
          },
          (error) => {
            console.error('Errore geolocalizzazione:', error);
            setUserLocation(prev => prev);
          },
          options
        );
    
        return watchId;
    }, []);
    
    useEffect(() => {
        fetchPointsOfInterest(); // Aggiungiamo questa chiamata
        const watchId = fetchUserLocation();
        
        return () => {
          if (watchId) {
            navigator.geolocation.clearWatch(watchId);
          }
        };
    }, [fetchUserLocation, fetchPointsOfInterest]);

    // Funzione per ordinare i POI
    const sortPointsOfInterest = (poiList) => {
        return poiList.sort((a, b) => {
            if (sortOption === 'distance' && userLocation) {
                const latLngA = a.field_geolocalizzazione && a.field_geolocalizzazione[0];
                const latLngB = b.field_geolocalizzazione && b.field_geolocalizzazione[0];
                const distanceA = latLngA ? calculateDistance(userLocation.latitude, userLocation.longitude, latLngA.lat, latLngA.lng) : Infinity;
                const distanceB = latLngB ? calculateDistance(userLocation.latitude, userLocation.longitude, latLngB.lat, latLngB.lng) : Infinity;
                return distanceA - distanceB;
            } else if (sortOption === 'alphabetical') {
                return a.title[0].value.localeCompare(b.title[0].value);
            }
            return 0; // Default (nessun ordinamento specifico)
        });
    };

    // Funzione per filtrare per categoria
    const filterByCategory = (poiList) => {
        const filteredList = selectedCategory 
            ? poiList.filter(poi => 
                poi.field_tipologia_poi && 
                poi.field_tipologia_poi[0].target_id === selectedCategory
            ) 
            : poiList;
    
        return filteredList;
    };

    
    if (loading) {
        return <LoadingSkeletonList />;
      }
    

    // Ordina e filtra i punti di interesse prima di renderizzarli
    let sortedAndFilteredPoints = filterByCategory(pointsOfInterest);
    sortedAndFilteredPoints = sortPointsOfInterest(sortedAndFilteredPoints);

    return (
        <main className="container mb-5 mt-2">
            <BackButton />
            <div className="d-flex justify-content-end gap-3 mt-2 me-2">
                <div className="dropdown">
                    <button className="btn dropdown-button dropdown-toggle" type="button" id="dropdownMenuButton" data-bs-toggle="dropdown" aria-expanded="false">
                        Ordina
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                        <li><button className="dropdown-item" onClick={() => setSortOption('alphabetical')}>Alfabetico</button></li>
                        <li><button className="dropdown-item" onClick={() => setSortOption('distance')}>Km</button></li>
                    </ul>
                </div>

                <div className="dropdown">
                    <button className="btn dropdown-button dropdown-toggle" type="button" id="dropdownMenuButton2" data-bs-toggle="dropdown" aria-expanded="false">
                        Categorie
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton2">
                        {Object.keys(taxonomyData).map(tid => (
                            <li key={tid}>
                                <button className="dropdown-item" onClick={() => setSelectedCategory(tid)}>
                                    {taxonomyData[tid].title}
                                </button>
                            </li>
                        ))}
                        <li><button className="dropdown-item" onClick={() => setSelectedCategory(null)}>Tutte le categorie</button></li>
                    </ul>
                </div>
            </div>

            <section className="mt-4">
                <h2>Lista dei punti di interesse</h2>
                <p>Esplora, scopri e pianifica il tuo tour con facilità!</p>
            </section>

            <section>
            <ul className="list-group mb-5">
                {sortedAndFilteredPoints.map((poi, index) => {
                    const latLng = poi.field_geolocalizzazione && poi.field_geolocalizzazione[0];
                    let distance = null;

                    if (latLng && userLocation) {
                    const { lat, lng } = latLng;
                    distance = calculateDistance(userLocation.latitude, userLocation.longitude, lat, lng);
                    }

                    return (
                    <li key={index} className="list-group-item list-group-item-divider">
                        <PoiCardList 
                        poi={poi} 
                        taxonomyData={taxonomyData} 
                        distance={distance ? distance.toFixed(2) + ' km' : ' - '}
                        />
                    </li>
                    );
                })}
            </ul>
            </section>
        </main>
    );
};

export default List;